import { Checkbox } from "@/components/flexkit/checkbox/Checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/flexkit/tables/Table";
import { cn } from "@/components/podkit/lib/cn";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { DropdownMenuItem } from "@/components/podkit/dropdown/DropDown";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Tooltip } from "@/components/Tooltip";
import { GroupAvatar } from "@/components/settings/organization/groups/GroupAvatar";
import { MemberAvatarStack } from "@/components/settings/organization/groups/MemberAvatarStack";
import { DeleteGroupDialog } from "@/components/settings/organization/groups/DeleteGroupDialog";
import { IconEdit } from "@/assets/icons/geist/IconEdit";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { IconUserAdd } from "@/assets/icons/geist/IconUserAdd";
import {
    groupsQueryKey,
    useCreateRoleAssignment,
    useDeleteRoleAssignment,
    useListGroupMemberships,
    type PlainRoleAssignment,
} from "@/queries/group-queries";
import { useMembersByIds, useOrganization } from "@/queries/organization-queries";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { ORG_ROLES, getOrgRoleLabel } from "@/utils/org-roles";
import { formatError } from "@/utils/errors";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Group, ResourceRole } from "gitpod-next-api/gitpod/v1/group_pb";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { MoreHorizontal } from "lucide-react";
import { useState, useMemo, useCallback, useRef, type FC } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

const MAX_VISIBLE_GROUP_MEMBERS = 3;

export type RoleAssignmentsData = {
    assignments: PlainRoleAssignment[];
};

type GroupsTableBodyProps = {
    groups: PlainMessage<Group>[];
    searchValue: string;
    isLoading?: boolean;
    roleAssignments?: RoleAssignmentsData;
    onAddMembers: (group: PlainMessage<Group>) => void;
    onEdit: (group: PlainMessage<Group>) => void;
};

/**
 * Table view for the groups list with inline role checkboxes.
 * Receives already-filtered groups, pre-fetched role assignments, and callbacks from the parent.
 */
export const GroupsTableBody: FC<GroupsTableBodyProps> = ({ groups, searchValue, isLoading, roleAssignments, onAddMembers, onEdit }) => {
    const { data: organization } = useOrganization();
    const orgId = organization?.id ?? "";

    // Index assignments by groupId for O(1) lookup per row
    const assignmentsByGroupId = useMemo(() => {
        const map = new Map<string, PlainRoleAssignment[]>();
        for (const a of roleAssignments?.assignments ?? []) {
            const existing = map.get(a.groupId) ?? [];
            existing.push(a);
            map.set(a.groupId, existing);
        }
        return map;
    }, [roleAssignments?.assignments]);

    return (
        <Table className="mt-8">
            <TableHeader className="[&_th]:border-b [&_th]:border-border-light [&_th]:bg-transparent">
                <TableRow className="text-base">
                    <TableHead>Name</TableHead>
                    {ORG_ROLES.map((role) => (
                        <TableHead key={role.role} className="text-center">
                            <Tooltip content={role.description} usePortal>
                                <span className="cursor-default leading-tight">
                                    {role.label}
                                </span>
                            </Tooltip>
                        </TableHead>
                    ))}
                    <TableHead>Members</TableHead>
                    <TableHead className="w-10" data-no-side-padding />
                </TableRow>
            </TableHeader>
            <TableBody className="text-base">
                {isLoading ? (
                    <TableRow>
                        <TableCell
                            colSpan={ORG_ROLES.length + 3}
                            className="py-8 text-center text-sm text-content-secondary"
                        >
                            Loading groups…
                        </TableCell>
                    </TableRow>
                ) : (
                    <>
                        {groups.map((group) => (
                            <GroupRow
                                key={group.id}
                                group={group}
                                orgId={orgId}
                                assignments={assignmentsByGroupId.get(group.id) ?? []}
                                onAddMembers={() => onAddMembers(group)}
                                onEdit={() => onEdit(group)}
                            />
                        ))}
                        {groups.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={ORG_ROLES.length + 3}
                                    className="py-8 text-center text-sm text-content-secondary"
                                >
                                    {searchValue.trim()
                                        ? "No groups match your search."
                                        : "No groups found."}
                                </TableCell>
                            </TableRow>
                        )}
                    </>
                )}
            </TableBody>
        </Table>
    );
};

type GroupRowProps = {
    group: PlainMessage<Group>;
    orgId: string;
    assignments: PlainRoleAssignment[];
    onAddMembers: () => void;
    onEdit: () => void;
};

const GroupRow: FC<GroupRowProps> = ({ group, orgId, assignments, onAddMembers, onEdit }) => {
    // Fetch only enough memberships for the avatar stack (maxVisible + 1 to detect overflow)
    const { data: groupMemberships = [] } = useListGroupMemberships(group.id, MAX_VISIBLE_GROUP_MEMBERS + 1);
    const { hasPermission } = useResourcePermissions();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    const memberUserIds = useMemo(
        () =>
            groupMemberships
                .slice(0, MAX_VISIBLE_GROUP_MEMBERS)
                .map((m) => m.subject?.id)
                .filter((id): id is string => !!id),
        [groupMemberships],
    );
    const { members: membersMap } = useMembersByIds(memberUserIds);

    const canAddMembers = hasPermission(ResourceType.GROUP, group.id, "groupmembership:create");
    const canEdit = hasPermission(ResourceType.GROUP, group.id, "group:update");
    const canDelete = hasPermission(ResourceType.GROUP, group.id, "group:delete");

    return (
        <>
            <TableRow className="group/row">
                {/* Name cell — padding on Link for larger click area */}
                <TableCell className="!p-0">
                    <Link
                        to={`/settings/members/group/${group.id}`}
                        className="flex items-center gap-3 px-4 py-2"
                        data-tracking-id="view-group-details-table-row"
                    >
                        <GroupAvatar groupName={group.name} size="sm" />
                        <div className="min-w-0">
                            <div className="truncate text-base font-bold text-content-primary">{group.name}</div>
                            {group.description && (
                                <div className="truncate text-base text-content-secondary">{group.description}</div>
                            )}
                        </div>
                    </Link>
                </TableCell>

                {/* Role checkbox cells */}
                {ORG_ROLES.map((roleConfig) => (
                    <TableCell key={roleConfig.role} className="text-center">
                        <RoleCheckboxCell
                            group={group}
                            role={roleConfig.role}
                            orgId={orgId}
                            assignments={assignments}
                        />
                    </TableCell>
                ))}

                {/* Members cell */}
                <TableCell>
                    <MemberAvatarStack
                        orgMembers={Array.from(membersMap.values())}
                        memberships={groupMemberships}
                        maxVisible={MAX_VISIBLE_GROUP_MEMBERS}
                        totalCount={group.memberCount}
                        size="sm"
                    />
                </TableCell>

                {/* Actions cell */}
                <TableCell className="!text-left" data-no-side-padding>
                    <div
                        className={cn(
                            "opacity-0 transition-opacity group-hover/row:opacity-100 group-focus-within/row:opacity-100",
                            { "opacity-100": menuOpen },
                        )}
                    >
                        <DropdownActions
                            onOpenChange={setMenuOpen}
                            button={
                                <button
                                    className="flex size-8 items-center justify-center rounded-md border border-border-base hover:bg-surface-secondary"
                                    aria-label="Group actions"
                                    data-tracking-id="group-actions"
                                >
                                    <MoreHorizontal className="size-4 text-content-tertiary" />
                                </button>
                            }
                            align="end"
                        >
                            <DropdownMenuItem
                                key="add-members"
                                data-tracking-id="group-add-member"
                                disabled={group.systemManaged || !canAddMembers}
                                onClick={onAddMembers}
                            >
                                <IconUserAdd size="sm" className="mr-2" />
                                Add Members
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                key="edit"
                                data-tracking-id="group-edit"
                                disabled={group.systemManaged || !canEdit}
                                onClick={onEdit}
                            >
                                <IconEdit size="sm" className="mr-2" />
                                Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                key="delete"
                                data-tracking-id="group-delete"
                                className="text-content-danger"
                                disabled={group.systemManaged || !canDelete}
                                onClick={() => setShowDeleteDialog(true)}
                            >
                                <IconTrash size="sm" className="mr-2" />
                                Delete
                            </DropdownMenuItem>
                        </DropdownActions>
                    </div>
                </TableCell>
            </TableRow>

            {showDeleteDialog && (
                <DeleteGroupDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog} group={group} />
            )}
        </>
    );
};

type RoleCheckboxCellProps = {
    group: PlainMessage<Group>;
    role: ResourceRole;
    orgId: string;
    assignments: PlainRoleAssignment[];
};

const RoleCheckboxCell: FC<RoleCheckboxCellProps> = ({ group, role, orgId, assignments }) => {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const { hasPermission } = useResourcePermissions();
    const canManageOrgRoles = hasPermission(ResourceType.ORGANIZATION, "", "org:update");

    const createRoleAssignment = useCreateRoleAssignment();
    const deleteRoleAssignment = useDeleteRoleAssignment();
    const isPending = createRoleAssignment.isPending || deleteRoleAssignment.isPending;
    const isPendingRef = useRef(false);
    isPendingRef.current = isPending;

    const { isAssigned, assignmentId } = useMemo(() => {
        const match = assignments.find((a) => a.resourceRole === role);
        return { isAssigned: !!match, assignmentId: match?.id };
    }, [assignments, role]);

    const handleToggle = useCallback(
        async (checked: boolean) => {
            if (!orgId || isPendingRef.current) return;

            const roleLabel = getOrgRoleLabel(role) ?? "role";

            try {
                if (checked) {
                    await createRoleAssignment.mutateAsync({
                        groupId: group.id,
                        resourceType: ResourceType.ORGANIZATION,
                        resourceId: orgId,
                        resourceRole: role,
                    });
                } else if (assignmentId) {
                    await deleteRoleAssignment.mutateAsync({
                        assignmentId,
                        resourceType: ResourceType.ORGANIZATION,
                        resourceId: orgId,
                    });
                }

                // Invalidate the single org-level query (no groupId filter)
                await queryClient.invalidateQueries({
                    queryKey: groupsQueryKey.roleAssignments(ResourceType.ORGANIZATION, orgId),
                });

                toast({
                    title: checked ? "Role assigned" : "Role removed",
                    description: checked
                        ? `${roleLabel} assigned to ${group.name}.`
                        : `${roleLabel} removed from ${group.name}.`,
                });
            } catch (error) {
                toast({
                    title: "Error",
                    description: `Failed to update role: ${formatError(error)}`,
                });
            }
        },
        [orgId, group.id, group.name, role, assignmentId, createRoleAssignment, deleteRoleAssignment, queryClient, toast],
    );

    return (
        <div className="flex items-center justify-center">
            <Checkbox
                checked={isAssigned}
                disabled={!canManageOrgRoles || isPending || group.systemManaged}
                onCheckedChange={(checked) => void handleToggle(checked === true)}
                aria-label={`Toggle ${ORG_ROLES.find((r) => r.role === role)?.label ?? "role"} for ${group.name}`}
                data-tracking-id={`group-role-toggle-${role}`}
                className="size-5 data-[state=unchecked]:border-border-strong"
            />
        </div>
    );
};
